import { Box, Button, Dialog, Flex, Text, TextField, IconButton } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import { BiChevronUp, BiChevronDown } from 'react-icons/bi';

const AdvancedAlarmSettings = ({ isOpen, onClose, alarmType, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState({
    timeOutOnNotification: 5,
    timeOutOnConfirmation: 5,
    timeOutOnHold: 5,
    isHoldTimeExtension: 5,
    issueWarningBefore: 2
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        timeOutOnNotification: settings.timeOutOnNotification || 5,
        timeOutOnConfirmation: settings.timeOutOnConfirmation || 5,
        timeOutOnHold: settings.timeOutOnHold || 5,
        isHoldTimeExtension: settings.isHoldTimeExtension || 5,
        issueWarningBefore: Math.min(
          settings.issueWarningBefore || 2,
          Math.floor((settings.isHoldTimeExtension || 5) / 2)
        )
      });
    }
  }, [settings, isOpen]);

  const adjustValue = (field, increment, min = 5, max = 60, step = 5) => {
    setLocalSettings(prev => {
      const currentValue = prev[field];
      const dynamicMax = field === 'issueWarningBefore' ? Math.floor(prev.isHoldTimeExtension / 2) : max;
      let newValue = currentValue + increment;
      
      // Apply step constraint
      if (field === 'issueWarningBefore') {
        newValue = Math.max(min, Math.min(dynamicMax, newValue));
      } else {
        newValue = Math.max(min, Math.min(max, Math.round(newValue / step) * step));
      }
      
      return {
        ...prev,
        [field]: newValue
      };
    });
  };

  const handleInputChange = (field, value, min = 5, max = 60, step = 5) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    
    const dynamicMax = field === 'issueWarningBefore' ? Math.floor(localSettings.isHoldTimeExtension / 2) : max;
    let constrainedValue;
    if (field === 'issueWarningBefore') {
      constrainedValue = Math.max(min, Math.min(dynamicMax, numValue));
    } else {
      constrainedValue = Math.max(min, Math.min(max, Math.round(numValue / step) * step));
    }
    
    setLocalSettings(prev => ({
      ...prev,
      [field]: constrainedValue
    }));
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const getFormattedAlarmType = (alarmType = '') => {
    return alarmType.charAt(0).toUpperCase() +
           alarmType.slice(1).replace(/([A-Z])/g, ' $1');
  };

  const getRangeLabel = (field) => {
    const max = field.key === 'issueWarningBefore'
      ? Math.floor(localSettings.isHoldTimeExtension / 2)
      : field.max;

    return `${field.min}-${max} min`;
  };

  const timeoutFields = [
    {
      key: 'timeOutOnNotification',
      label: 'Timeout for Notification',
      min: 5,
      max: 60,
      step: 5,
      description: 'Notification expiry'
    },
    {
      key: 'timeOutOnConfirmation',
      label: 'Timeout for Confirmation',
      min: 5,
      max: 60,
      step: 5,
      description: 'Confirmation expiry'
    },
    {
      key: 'timeOutOnHold',
      label: 'Timeout for Hold',
      min: 5,
      max: 60,
      step: 5,
      description: 'Hold expiry'
    },
    {
      key: 'isHoldTimeExtension',
      label: 'Extended Time for Hold',
      min: 5,
      max: 60,
      step: 5,
      description: 'Extra hold duration'
    },
    {
      key: 'issueWarningBefore',
      label: 'Time Before Issue Warning',
      min: 2,
      step: 1,
      description: 'Warning threshold'
    }
  ];

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content maxWidth="720px">
        <Dialog.Title>
          Advanced Settings - {getFormattedAlarmType(alarmType)}
        </Dialog.Title>
        
        <Box mt="4">
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '14px'
            }}
          >
            {timeoutFields.map((field) => (
              <Box
                key={field.key}
                style={{
                  border: '1px solid var(--gray-4)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--gray-1)',
                  padding: '12px'
                }}
              >
                <Flex justify="between" align="start" gap="3" mb="2">
                  <Box>
                    <Text as="div" size="2" weight="bold">
                      {field.label}
                    </Text>
                    <Text as="div" size="1" color="gray">
                      {field.description}
                    </Text>
                  </Box>
                  <Text size="1" color="gray" style={{ whiteSpace: 'nowrap' }}>
                    {getRangeLabel(field)}
                  </Text>
                </Flex>

                <Flex gap="2" align="center">
                  <Flex align="center" gap="1" style={{ minWidth: '96px' }}>
                    <TextField.Root
                      type="number"
                      min={field.min}
                      max={field.key === 'issueWarningBefore' ? Math.floor(localSettings.isHoldTimeExtension / 2) : field.max}
                      step={field.step}
                      value={localSettings[field.key]}
                      onChange={(e) => handleInputChange(field.key, e.target.value, field.min, field.max, field.step)}
                      placeholder="00"
                      style={{ width: '60px', textAlign: 'center' }}
                    />
                    <Flex direction="column">
                      <IconButton 
                        type="button" 
                        size="1" 
                        variant="ghost" 
                        onClick={() => adjustValue(field.key, field.step, field.min, field.max, field.step)}
                      >
                        <BiChevronUp />
                      </IconButton>
                      <IconButton 
                        type="button" 
                        size="1" 
                        variant="ghost" 
                        onClick={() => adjustValue(field.key, -field.step, field.min, field.max, field.step)}
                      >
                        <BiChevronDown />
                      </IconButton>
                    </Flex>
                  </Flex>
                  <Text size="2" color="gray">minutes</Text>
                </Flex>
              </Box>
            ))}
          </Box>
        </Box>

        <Flex justify="end" mt="6">
          <Flex gap="2">
            <Button variant="soft" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AdvancedAlarmSettings;
